<template>
  <div class="vehicle-number-input" :class="{ 'vehicle-number-input_compact': compact }">
    <div v-if="!compact" class="vehicle-number-input__label-row">
      <v-icon small class="vehicle-number-input__icon grey--text">mdi-car</v-icon>
      <span class="vehicle-number-input__label">{{ label }}</span>
      <template v-if="required">
        <span class="vehicle-number-input__asterisk error--text">*</span>
        <span class="vehicle-number-input__required error--text text-caption">обязательно</span>
      </template>
    </div>
    <div class="vehicle-number-input__field-row">
      <v-text-field
        ref="field"
        :value="displayValue"
        :placeholder="effectivePlaceholder"
        :disabled="disabled"
        :outlined="outlined"
        :dense="dense"
        :hide-details="hideDetails"
        :rules="compact ? [] : computedRules"
        :maxlength="nonStandard ? 20 : 12"
        :counter="!compact && nonStandard ? 20 : undefined"
        :prepend-inner-icon="prependInnerIcon"
        :loading="loading"
        :clearable="clearable"
        class="vehicle-number-input__field"
        @focus="isFocused = true"
        @blur="isFocused = false"
        @keydown="onKeydown"
        @input="onInput"
        @keyup.enter="$emit('enter')"
      />
      <v-checkbox
        v-if="!compact"
        v-model="nonStandard"
        label="Нестандартный номер"
        hide-details
        class="vehicle-number-input__checkbox mt-0"
        color="primary"
        @change="onNonStandardChange"
      />
    </div>
  </div>
</template>

<script>
const STANDARD_LETTERS = 'АВЕКМНОРСТУХABEKMHOPCTYX';
const STANDARD_PATTERN = /^[АВЕКМНОРСТУХABEKMHOPCTYX]\d{3}[АВЕКМНОРСТУХABEKMHOPCTYX]{2}\d{2,3}$/i;
const NON_STANDARD_PATTERN = /^[0-9АВЕКМНОРСТУХA-Za-z]*$/u;

function isAllowedLetter (c) {
  return STANDARD_LETTERS.includes(String(c).toUpperCase());
}

function isDigit (c) {
  return /\d/.test(c);
}

export default {
  name: 'VehicleNumberInput',
  props: {
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Номер ТС',
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: true,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    compact: {
      type: Boolean,
      default: false,
    },
    prependInnerIcon: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      nonStandard: false,
      isFocused: false,
    };
  },
  computed: {
    effectivePlaceholder () {
      if (this.compact && this.isFocused && !this.rawValue) {
        return this.nonStandard ? 'Введите номер' : 'A 000 AA 000';
      }
      return this.placeholder || (this.nonStandard ? 'Введите номер' : 'A 000 AA 000');
    },
    rawValue () {
      return (this.value || '').replace(/\s/g, '');
    },
    displayValue () {
      const raw = (this.rawValue || '').toUpperCase();
      if (this.nonStandard) return raw;
      if (raw.length <= 1) return raw;
      if (raw.length <= 4) return `${raw.slice(0, 1)} ${raw.slice(1)}`;
      if (raw.length <= 6) return `${raw.slice(0, 1)} ${raw.slice(1, 4)} ${raw.slice(4)}`;
      return `${raw.slice(0, 1)} ${raw.slice(1, 4)} ${raw.slice(4, 6)} ${raw.slice(6)}`;
    },
    computedRules () {
      const list = [...(this.rules || [])];
      if (this.required) {
        list.push(v => !!((v || '').toString().replace(/\s/g, '').trim()) || 'Обязательное поле');
      }
      list.push(v => {
        const s = (v || '').toString().replace(/\s/g, '').trim().toUpperCase();
        if (!s) return true;
        if (this.nonStandard) {
          return (NON_STANDARD_PATTERN.test(s) && s.length <= 20) || 'Только цифры и буквы (А, В, Е, К, М, Н, О, Р, С, Т, У, Х), до 20 символов';
        }
        return STANDARD_PATTERN.test(s) || 'Формат: A 000 AA 000';
      });
      return list;
    },
  },
  methods: {
    /** Маска A 000 AA 000: 1 буква, 3 цифры, 2 буквы, 2–3 цифры (регион); буквы в верхний регистр */
    applyStandardMask (raw) {
      const s = (raw || '').replace(/\s/g, '').toUpperCase();
      let out = '';
      let i = 0;
      while (i < s.length && !isAllowedLetter(s[i])) i++;
      if (i < s.length) {
        out += s[i];
        i++;
      }
      let n = 0;
      while (i < s.length && n < 3) {
        if (isDigit(s[i])) {
          out += s[i];
          n++;
        }
        i++;
      }
      n = 0;
      while (i < s.length && n < 2) {
        if (isAllowedLetter(s[i])) {
          out += s[i];
          n++;
        }
        i++;
      }
      n = 0;
      while (i < s.length && n < 3) {
        if (isDigit(s[i])) {
          out += s[i];
          n++;
        }
        i++;
      }
      return out;
    },
    /** Для стандартной маски: тип символа на позиции 0..8 (A 000 AA 00[0]) */
    expectedTypeAt (rawIndex) {
      if (rawIndex < 0 || rawIndex > 8) return null;
      if (rawIndex === 0 || rawIndex === 4 || rawIndex === 5) return 'letter';
      return 'digit';
    },
    displayToRawIndex (displayStr, displayIndex) {
      const s = (displayStr || '').slice(0, displayIndex);
      return s.replace(/\s/g, '').length;
    },
    onKeydown (e) {
      if (this.nonStandard) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const key = e.key;
      if (key.length !== 1) return;
      const input = e.target;
      const displayStr = (this.displayValue || '');
      const rawIndex = this.displayToRawIndex(displayStr, input.selectionStart || 0);
      if (rawIndex >= 9) {
        e.preventDefault();
        return;
      }
      const expected = this.expectedTypeAt(rawIndex);
      if (!expected) return;
      if (isDigit(key)) {
        if (expected !== 'digit') e.preventDefault();
        return;
      }
      if (isAllowedLetter(key.toUpperCase())) {
        if (expected !== 'letter') e.preventDefault();
        return;
      }
      e.preventDefault();
    },
    onInput (displayStr) {
      const noSpaces = (displayStr || '').replace(/\s/g, '');
      if (this.nonStandard) {
        const filtered = noSpaces.toUpperCase().replace(/[^0-9АВЕКМНОРСТУХA-Za-z]/g, '').slice(0, 20);
        this.$emit('input', filtered);
        return;
      }
      this.$emit('input', this.applyStandardMask(noSpaces));
    },
    onNonStandardChange () {
      const raw = (this.rawValue || '').toString().replace(/\s/g, '').toUpperCase();
      if (this.nonStandard) {
        const filtered = raw.replace(/[^0-9АВЕКМНОРСТУХA-Za-z]/g, '').slice(0, 20);
        this.$emit('input', filtered);
      } else {
        const standard = this.applyStandardMask(raw);
        const toEmit = standard.length > 0 ? standard : raw.slice(0, 9);
        this.$emit('input', toEmit);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.vehicle-number-input {
  &_compact .vehicle-number-input__field-row {
    gap: 0;
  }
  &_compact .vehicle-number-input__field {
    min-width: 0;
  }
  &__label-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
  }
  &__icon {
    margin-right: 2px;
  }
  &__label {
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.6);
  }
  &__asterisk {
    margin-left: 2px;
  }
  &__required {
    margin-left: 2px;
  }
  &__field-row {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
  }
  &__field {
    flex: 1;
    min-width: 200px;
  }
  &__checkbox {
    flex-shrink: 0;
  }
}
</style>
